import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { DocumentTest } from "../../documentTests/documentTestsApi";


interface Dict {
    titleTests: string;
    titleSpec: string;
    section: string;
    lp: string;
    test: string;
    description: string;
    methodology: string;
    planned: string;
    lastRun: string;
    outcome: string;
    assignments: string;
    role: string;
    person: string;
    email: string;
    company: string;
    noTests: string;
    noAssignments: string;
    noSpec: string;
    phases: string; // legacy label
    phaseLabel: string; // "Etap" / "Stage"
    steps: string;
    criteria: string;
    criteriaName: string; // "Nazwa" / "Name"
    result: string; // "Wynik" / "Result"
    comment: string; // "Komentarz" / "Comment"
    summary: string; // "Podsumowanie" / "Summary"
    approvedBy: string; // "Zatwierdził" / "Approved by"
    executedBy: string; // "Wykonał" / "Executed by"
    date: string; // "Data" / "Date"
    yes: string;
    no: string;
    na: string;
    none: string;
    testFlow: string
}


const T = (lang: string): Dict =>
    lang === "pl"
        ? {
            titleTests: "Testy dokumentu",
            titleSpec: "Test",
            section: "Sekcja",
            lp: "Lp.",
            test: "Test",
            description: "Opis",
            methodology: "Metodologia",
            planned: "Planowany termin",
            lastRun: "Ostatnie uruchomienie",
            outcome: "Wynik",
            assignments: "Przypisania",
            role: "Rola",
            person: "Osoba",
            email: "E-mail",
            company: "Firma",
            noTests: "Brak testów do wyświetlenia.",
            noAssignments: "Brak przypisań.",
            noSpec: "Brak szczegółów specyfikacji.",
            phases: "Fazy",
            phaseLabel: "Etap",
            steps: "Krok",
            criteria: "Kryterium akceptacji",
            criteriaName: "Nazwa",
            result: "Wynik",
            comment: "Komentarz",
            summary: "Podsumowanie",
            approvedBy: "Zatwierdził",
            executedBy: "Wykonał",
            date: "Data",
            yes: "Tak",
            no: "Nie",
            na: "N/D",
            none: "—",
            testFlow: "Przebieg testu"
        }
        : {
            titleTests: "Document tests",
            titleSpec: "Test specification",
            section: "Section",
            lp: "No.",
            test: "Test",
            description: "Description",
            methodology: "Methodology",
            planned: "Planned window",
            lastRun: "Last run",
            outcome: "Outcome",
            assignments: "Assignments",
            role: "Role",
            person: "Person",
            email: "Email",
            company: "Company",
            noTests: "No tests to display.",
            noAssignments: "No assignments.",
            noSpec: "No specification details.",
            phases: "Phases",
            phaseLabel: "Stage",
            steps: "Steps",
            criteria: "Acceptance criteria",
            criteriaName: "Name",
            result: "Result",
            comment: "Comment",
            summary: "Summary",
            approvedBy: "Approved by",
            executedBy: "Executed by",
            date: "Date",
            yes: "Yes",
            no: "No",
            na: "N/A",
            none: "—",
            testFlow: "Test flow"
        };

// ==== helpers ====
function fmtDate(d?: string | null): string {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d!;
    // YYYY-MM-DD HH:mm (local)
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

function fmtPlanned(a?: string | null, b?: string | null): string {
    if (!a && !b) return "";
    if (a && !b) return fmtDate(a);
    if (!a && b) return fmtDate(b);
    return `${fmtDate(a!)} – ${fmtDate(b!)}`;
}

function hasRichHtml(value?: string | null): value is string {
    return typeof value === "string" && value.trim().length > 0;
}



// ==== Component ====
export interface PrintPreviewDocumentTestMethodologyViewProps {
    tests: DocumentTest[];
    lang: string;
}

export const PrintPreviewDocumentTestMethodologyView: React.FC<PrintPreviewDocumentTestMethodologyViewProps> = ({ tests, lang }) => {
    const t = T(lang);

    const activeTests = useMemo(() => (tests ?? []).slice().sort((a, b) => {
        const sa = (a.sectionName ?? "").localeCompare(b.sectionName ?? "");
        if (sa !== 0) return sa;
        return (a.order ?? 0) - (b.order ?? 0);
    }), [tests]);

    // grupowanie po sekcji
    const bySection = useMemo(() => {
        const map = new Map<string, DocumentTest[]>();
        for (const test of activeTests) {
            const key = `${test.sectionId ?? ""}|${test.sectionName ?? ""}`;
            const arr = map.get(key) ?? [];
            arr.push(test);
            map.set(key, arr);
        }
        return Array.from(map.entries()).map(([k, arr]) => {
            const [, secName] = k.split("|");
            return { sectionName: secName || "", items: arr };
        });
    }, [activeTests]);

    return (
        <Box
            sx={{
                backgroundColor: "#fff",
                p: 2,
                fontFamily: `Arial, "Helvetica Neue", Helvetica, sans-serif`,
                lineHeight: 1.6,
                color: "#000",
                "& h1": { fontSize: "1.25rem", margin: "0 0 8px 0" },
                "& section": { pageBreakInside: "avoid" },
                "& table": {
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "16px",
                    pageBreakInside: "avoid",
                },
                "& th, & td": {
                    border: "1px solid #000",
                    padding: "6px 8px",
                    verticalAlign: "top",
                    fontSize: "0.9rem",
                    wordBreak: "break-word",
                },
                "& th": { backgroundColor: "#f0f0f0", fontWeight: 700, textAlign: "left" },
                "@media print": { border: "none", p: 0 },
                "& .ok": { color: "#2e7d32", fontWeight: 700 },
                "& .bad": { color: "#d32f2f", fontWeight: 700 },
            }}
        >


            {/* ====== Szczegóły specyfikacji dla każdego testu ====== */}
            {activeTests.map((test, idx) => (
                <section key={`spec-${test.id}`} style={{ marginBottom: 24 }}>
                    <table aria-label={`spec-${test.id}`}>
                        <thead>
                            <tr>
                                <th colSpan={4} style={{ backgroundColor: "#eaeaea", fontWeight: 700, fontSize: "1rem" }}>
                                    {test.order}. {t.titleSpec}: {hasRichHtml(test?.testSpecification.name)
                                        ? <span dangerouslySetInnerHTML={{ __html: test.testSpecification.name }} />
                                        : t.none}
                                </th>
                            </tr>
                            <tr>
                                <th colSpan={4} style={{ backgroundColor: "#eaeaea", fontWeight: 700, fontSize: "1rem" }}>
                                    {t.description}
                                </th>
                            </tr>

                            <tr>
                                <td colSpan={4}>
                                    {hasRichHtml(test.testSpecification.description)
                                        ? (
                                            <div
                                                style={{ fontSize: "0.9rem", color: "#444", minHeight: 24 }}
                                                dangerouslySetInnerHTML={{ __html: test.testSpecification.description }}
                                            />
                                        )
                                        : (
                                            <div style={{ fontSize: "0.9rem", color: "#444", minHeight: 24 }}>
                                                {"\u00A0"}
                                            </div>
                                        )}
                                </td>
                            </tr>

                            <tr>
                                <th colSpan={4} style={{ backgroundColor: "#eaeaea", fontWeight: 700, fontSize: "1rem" }}>
                                    {t.methodology}
                                </th>
                            </tr>
                            <tr>
                                <td colSpan={4}>
                                    {hasRichHtml(test.testSpecification.metodology)
                                        ? (
                                            <div
                                                style={{ fontSize: "0.9rem", color: "#444", minHeight: 24 }}
                                                dangerouslySetInnerHTML={{ __html: test.testSpecification.metodology }}
                                            />
                                        )
                                        : (
                                            <div style={{ fontSize: "0.9rem", color: "#444", minHeight: 24 }}>
                                                {"\u00A0"}
                                            </div>
                                        )}
                                </td>
                            </tr>
                        </thead>
                        <tbody>

                            {/* Fazy / Kroki / Kryteria */}
                            {test.testSpecification?.testPhases && test.testSpecification.testPhases.length > 0 ? (
                                test.testSpecification.testPhases.map((ph, pIdx) => (
                                    <React.Fragment key={pIdx}>
                                        <TestPhasesBlock test={test} t={t} />

                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={3} style={{ fontStyle: "italic", color: "#777" }}>{t.noSpec}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Page break after each spec block except the last one */}
                    {
                        idx < activeTests.length - 1 && (
                            <div style={{ pageBreakAfter: "always", margin: "8px 0" }} />
                        )
                    }
                </section>
            ))
            }
        </Box >
    );
};

type PhasesBlockProps = {
    test: DocumentTest;
    t: Dict;
};

const TestPhasesBlock: React.FC<PhasesBlockProps> = ({ test, t }) => {
    const phases = test.testSpecification?.testPhases ?? [];
    let lpCounter = 1;

    if (phases.length === 0) {
        return (
            <>
                <tr>
                    <td colSpan={4} style={{ fontStyle: "italic", color: "#777" }}>{t.noSpec}</td>
                </tr>
            </>
        );
    }
    return (
        <>
            {phases.map((ph, pIdx) => (
                <React.Fragment key={pIdx}>
                    <tr>
                        <th
                            colSpan={4}
                            style={{ textAlign: "center", padding: "4px 8px", background: "#f7f7f7" }}
                        >
                            <div
                                style={{
                                    fontSize: "0.9rem",
                                    color: "#444",
                                    minHeight: 24,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 8,
                                    flexWrap: "wrap",
                                }}
                            >
                                <span>{t.testFlow}</span>

                            </div>
                        </th>
                    </tr>


                    {/* Kroki + kryteria jako kolejne wiersze*/}
                    {(ph?.steps ?? [])
                        .slice()
                        .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
                        .map((st, sIdx) => (
                            <React.Fragment key={sIdx}>
                                {/* Wiersz kroku (kompaktowy) */}
                                <>
                                    <tr>
                                        <td colSpan={4}>
                                            <div style={{ fontWeight: 700 }}>
                                                {t.steps} {sIdx + 1}: {hasRichHtml(st?.name)
                                                    ? <span dangerouslySetInnerHTML={{ __html: st.name }} />
                                                    : t.none}
                                            </div>
                                        </td>
                                    </tr>
                                </>


                                {/* Kryteria jako oddzielne wiersze */}
                                <tr>
                                    <td colSpan={4} style={{ padding: 0, border: "none" }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <colgroup>
                                                <col style={{ width: "50px" }} />
                                                <col />
                                            </colgroup>
                                            <thead>
                                                <tr style={{ background: "#f7f7f7" }}>
                                                    <th style={{ textAlign: "center" }}>{t.lp}</th>
                                                    <th>   {t.criteria}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {((st?.criterias ?? []).length === 0) ? (
                                                    <tr>
                                                        <td colSpan={5} style={{ fontStyle: "italic", color: "#777" }}>{t.noSpec}</td>
                                                    </tr>
                                                ) : (
                                                    (st?.criterias ?? [])
                                                        .slice()
                                                        .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
                                                        .map((cr, cIdx) => (
                                                            <tr key={cIdx}>
                                                                <td style={{ textAlign: "center" }}>{lpCounter++}</td>
                                                                <td colSpan={4}>
                                                                    {hasRichHtml(cr?.name)
                                                                        ? <div dangerouslySetInnerHTML={{ __html: cr.name }} />
                                                                        : t.none}
                                                                </td>
                                                            </tr>
                                                        ))
                                                )}
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                </React.Fragment>
            ))}
        </>
    );
};